import instance from "../../apiInstance";

const PERIOD_MAP = {
  Hoje: "today",
  "7 Dias": "7d",
  "30 Dias": "30d",
  "12 Meses": "12m",
};

const resolvePeriod = (period) => {
  if (!period) return "30d";
  if (["today", "7d", "30d", "12m"].includes(period)) return period;
  return PERIOD_MAP[period] || "30d";
};

export const getAdminStats = async (period = "30 Dias") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/stats?period=${p}`);
  return res.data;
};

export const getAdminRanking = async (limit = 10, period = "30d") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/users/ranking?limit=${limit}&period=${p}`);
  return res.data;
};

export const getSignupsAnalytics = async (period = "30 Dias") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/analytics/signups?period=${p}`);
  return res.data;
};

export const getBidsAnalytics = async (period = "30 Dias") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/analytics/bids?period=${p}`);
  return res.data;
};

export const getAuctionsAnalytics = async () => {
  const res = await instance.get("/admin/analytics/auctions");
  return res.data;
};

export const getPointsAnalytics = async () => {
  const res = await instance.get("/admin/analytics/points");
  return res.data;
};

export const getRealtimeAnalytics = async (period = "30 Dias") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/analytics/realtime?period=${p}`);
  return res.data;
};

export const getAdminUsersList = async (skip = 0, limit = 20, search = "") => {
  const params = new URLSearchParams({ skip, limit });
  if (search) params.set("search", search);
  const res = await instance.get(`/admin/users/list?${params}`);
  return res.data;
};

export const getAdminUserDetails = async (userId) => {
  const res = await instance.get(`/admin/users/${userId}/details`);
  return res.data;
};

export const getUserActivity = async (userId) => {
  const res = await instance.get(`/admin/analytics/user-activity?user_id=${userId}`);
  return res.data;
};

export const getOnlineUsers = async () => {
  const res = await instance.get("/admin/analytics/online");
  return res.data;
};

export const getAccessHistory = async (limit = 50, period = "30d") => {
  const p = resolvePeriod(period);
  const res = await instance.get(`/admin/analytics/access-history?limit=${limit}&period=${p}`);
  return res.data;
};

export const getAdminAuctions = async (status) => {
  const params = status ? `?status=${status}` : "";
  const res = await instance.get(`/admin/auctions${params}`);
  return res.data;
};

export const createAdminAuction = async (data) => {
  const res = await instance.post("/admin/auctions", data);
  return res.data;
};

export const updateAdminAuction = async (id, data) => {
  const res = await instance.put(`/admin/auctions/${id}`, data);
  return res.data;
};

export const deleteAdminAuction = async (id) => {
  const res = await instance.delete(`/admin/auctions/${id}`);
  return res.data;
};

export const activateAdminAuction = async (id) => {
  const res = await instance.post(`/admin/auctions/${id}/activate`);
  return res.data;
};

export const endAdminAuction = async (id) => {
  const res = await instance.post(`/admin/auctions/${id}/end`);
  return res.data;
};

export const getAdminAuctionBids = async (id) => {
  const res = await instance.get(`/admin/auctions/${id}/bids`);
  return res.data;
};

export const getAdminAuctionWishes = async (seen) => {
  const params = seen !== undefined ? `?seen=${seen}` : "";
  const res = await instance.get(`/admin/auctions/wishes${params}`);
  return res.data;
};

export const markWishSeen = async (wishId) => {
  const res = await instance.put(`/admin/auctions/wishes/${wishId}/seen`);
  return res.data;
};

export const uploadAuctionImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await instance.post("/admin/auctions/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const uploadAuctionImagesBatch = async (files, onProgress) => {
  const formData = new FormData();
  files.forEach(file => formData.append("files", file));
  const res = await instance.post("/admin/auctions/upload-batch", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
};

// ── Promoções ──

export const getAdminPromos = async (active) => {
  const params = active !== undefined ? `?active=${active}` : "";
  const res = await instance.get(`/admin/promos${params}`);
  return res.data;
};

export const createAdminPromo = async (data) => {
  const res = await instance.post("/admin/promos", data);
  return res.data;
};

export const updateAdminPromo = async (id, data) => {
  const res = await instance.put(`/admin/promos/${id}`, data);
  return res.data;
};

export const deleteAdminPromo = async (id) => {
  const res = await instance.delete(`/admin/promos/${id}`);
  return res.data;
};

export const getPromoProducts = async (promoId) => {
  const res = await instance.get(`/admin/promos/${promoId}/products`);
  return res.data;
};

export const createPromoProduct = async (promoId, data) => {
  const res = await instance.post(`/admin/promos/${promoId}/products`, data);
  return res.data;
};

export const updatePromoProduct = async (productId, data) => {
  const res = await instance.put(`/admin/promos/products/${productId}`, data);
  return res.data;
};

export const deletePromoProduct = async (productId) => {
  const res = await instance.delete(`/admin/promos/products/${productId}`);
  return res.data;
};

export const uploadPromoImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const res = await instance.post("/admin/upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

export const uploadPromoImagesBatch = async (promoId, files, onProgress) => {
  const formData = new FormData();
  files.forEach(file => formData.append("files", file));
  const res = await instance.post(`/admin/promos/${promoId}/upload-batch`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return res.data;
};

export const generatePromoThumbnails = async () => {
  const res = await instance.post("/admin/promos/generate-thumbnails");
  return res.data;
};

// ── Roleta de Prêmios (Admin) ──

export const getSpinUsers = async (skip = 0, limit = 30, search = "") => {
  const params = new URLSearchParams({ skip, limit });
  if (search) params.set("search", search);
  const res = await instance.get(`/admin/spins/users?${params}`);
  return res.data;
};

export const grantSpin = async (userId, spins = 1, note = "", tierId = null, forcedPrizeKey = null) => {
  const payload = {
    user_id: userId,
    spins,
    note,
  };
  if (tierId) payload.tier_id = tierId;
  if (forcedPrizeKey) payload.forced_prize_key = forcedPrizeKey;

  const res = await instance.post("/admin/spins/grant", payload);
  return res.data;
};

export const revokeSpin = async (userId, spins = 1, note = "") => {
  const res = await instance.post("/admin/spins/revoke", {
    user_id: userId,
    spins,
    note,
  });
  return res.data;
};

export const getSpinHistory = async (status = "", skip = 0, limit = 50) => {
  const params = new URLSearchParams({ skip, limit });
  if (status) params.set("status", status);
  const res = await instance.get(`/admin/spins/history?${params}`);
  return res.data;
};

export const updateSpinStatus = async (resultId, status) => {
  const res = await instance.put(`/admin/spins/${resultId}/status`, { status });
  return res.data;
};

// ── Wheel Tiers (Tipos de Roleta) ──

export const getWheelTiers = async () => {
  const res = await instance.get("/admin/wheel/tiers");
  return res.data;
};

export const createWheelTier = async (data) => {
  const res = await instance.post("/admin/wheel/tiers", data);
  return res.data;
};

export const updateWheelTier = async (id, data) => {
  const res = await instance.put(`/admin/wheel/tiers/${id}`, data);
  return res.data;
};

export const deleteWheelTier = async (id) => {
  const res = await instance.delete(`/admin/wheel/tiers/${id}`);
  return res.data;
};

export const getTierPrizes = async (tierId) => {
  const res = await instance.get(`/admin/wheel/tiers/${tierId}/prizes`);
  return res.data;
};

export const createTierPrize = async (tierId, data) => {
  const res = await instance.post(`/admin/wheel/tiers/${tierId}/prizes`, data);
  return res.data;
};

export const updateTierPrize = async (prizeId, data) => {
  const res = await instance.put(`/admin/wheel/prizes/${prizeId}`, data);
  return res.data;
};

export const deleteTierPrize = async (prizeId) => {
  const res = await instance.delete(`/admin/wheel/prizes/${prizeId}`);
  return res.data;
};

export const setUserTier = async (userId, tierId) => {
  const res = await instance.put("/admin/wheel/user-tier", {
    user_id: userId,
    tier_id: tierId || null,
  });
  return res.data;
};

// ── Pesquisa pré-giro ──

export const getSurveyResponses = async (skip = 0, limit = 50) => {
  const res = await instance.get(`/admin/spins/survey?skip=${skip}&limit=${limit}`);
  return res.data;
};

export const getSurveySetting = async () => {
  const res = await instance.get("/admin/settings/spin-survey");
  return res.data;
};

export const toggleSpinSurvey = async (enabled) => {
  const res = await instance.put("/admin/settings/spin-survey", { enabled });
  return res.data;
};

// ── Depoimentos ──

export const getAdminTestimonials = async (status = "", skip = 0, limit = 50) => {
  const params = new URLSearchParams({ skip, limit });
  if (status) params.set("status", status);
  const res = await instance.get(`/admin/testimonials?${params}`);
  return res.data;
};

export const updateTestimonialStatus = async (id, status, note = "") => {
  const res = await instance.patch(`/admin/testimonials/${id}`, { status, note });
  return res.data;
};

export const deleteTestimonial = async (id) => {
  const res = await instance.delete(`/admin/testimonials/${id}`);
  return res.data;
};

// ── Gestão de Fotos (bags + variados) ──

export const getAdminImages = async (folder = null) => {
  const params = folder ? `?folder=${folder}` : "";
  const res = await instance.get(`/admin/images${params}`);
  return res.data;
};

export const toggleAdminImage = async (url, folder) => {
  const res = await instance.post("/admin/images/toggle", { url, folder });
  return res.data;
};

export const refreshImagesCache = async () => {
  const res = await instance.delete("/admin/images/cache");
  return res.data;
};
